import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Progress,
  Checkbox,
} from "@heroui/react";
import { useAuth } from "../contexts/AuthContext";
import {
  CloudArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { stateOptions, getCitiesForState, getCityOptions } from "../data/stateCityData";
import { VIDEO_TITLE_MAX_LENGTH } from "./videoConstants";

// Hard limits (will reject)
const MAX_SIZE_BYTES = 350 * 1024 * 1024; // 350MB
const MAX_DURATION_SECONDS = 240; // 4 minutes
const MAX_RESOLUTION_HEIGHT = 1080; // 1080p max

const VideoUploadHeroUI = ({ onUploadSuccess }) => {
  const { currentUser, getIdToken } = useAuth();
  const [step, setStep] = useState(0); // 0-select, 1-details, 2-upload
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [videoSize, setVideoSize] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState("");
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    title: "",
    description: "",
    state: "",
    city: ""
  });
  const [contentRulesAccepted, setContentRulesAccepted] = useState(false);
  const [warning, setWarning] = useState("");

  // Detect mobile for input optimization
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleFilePicked = (file) => {
    if (!file) return;

    // ✅ FIX: Clean up previous blob URL before creating a new one to prevent memory leaks
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }

    if (file.size > MAX_SIZE_BYTES) {
      setError("Video file must be 350MB or less");
      return;
    }
    setError("");
    setWarning("");
    setLoadingMetadata(true);
    setVideoFile(file);
    setVideoSize((file.size / (1024 * 1024)).toFixed(2));

    const blob = URL.createObjectURL(file);
    setVideoPreview(blob);

    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = function () {
      // Don't revoke the blob URL - we're still using it for the preview
      const duration = Math.round(v.duration);
      setVideoDuration(duration);

      // Check hard limits
      if (duration > MAX_DURATION_SECONDS) {
        setError(`Video duration (${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}) exceeds maximum of 4 minutes`);
        setLoadingMetadata(false);
        setVideoFile(null);
        setVideoPreview(null);
        if (blob) {
          URL.revokeObjectURL(blob);
        }
        return;
      }

      if (v.videoHeight > MAX_RESOLUTION_HEIGHT) {
        setError(`Video resolution (${v.videoWidth}x${v.videoHeight}) exceeds maximum of 1080p. Please use a video with 1080p or lower resolution.`);
        setLoadingMetadata(false);
        setVideoFile(null);
        setVideoPreview(null);
        if (blob) {
          URL.revokeObjectURL(blob);
        }
        return;
      }

      setLoadingMetadata(false);
      setStep(1);
    };
    v.onerror = function () {
      setError("Failed to load video metadata. Please try another file.");
      setLoadingMetadata(false);
      setVideoFile(null);
      setVideoPreview(null);
      if (blob) {
        URL.revokeObjectURL(blob);
      }
    };
    v.src = blob;
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    const f = e.dataTransfer.files?.[0];
    handleFilePicked(f);
  };

  const onBrowse = (e) => {
    const f = e.target.files?.[0];
    handleFilePicked(f);
  };

  const clearVideo = () => {
    // Clean up the blob URL to prevent memory leaks
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoFile(null);
    setVideoPreview(null);
    setVideoDuration(null);
    setVideoSize(null);
    setError("");
    setLoadingMetadata(false);
    setFieldErrors({ title: "", description: "", state: "", city: "" });
    setStep(0);
  };

  useEffect(() => {
    if (!selectedState) {
      setCityOptions([]);
      setSelectedCity("");
      return;
    }
    const cities = getCitiesForState(selectedState);
    setCityOptions(getCityOptions(cities));
    setSelectedCity("");
  }, [selectedState]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  const formatDuration = (seconds) => {
    if (!seconds) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const doUpload = async () => {
    if (!videoFile) {
      setError("Please select a video file");
      return;
    }

    // Clear previous field errors
    setFieldErrors({ title: "", description: "", state: "", city: "" });

    // Check individual fields and set specific errors
    const errors = {};
    if (!title || title.trim().length < 10) {
      errors.title = title.trim().length === 0 ? "Please enter a video title" : "Title must be at least 10 characters";
    }
    if (!description || description.trim().length < 20) {
      errors.description = description.trim().length === 0 ? "Please enter a description" : "Description must be at least 20 characters";
    }
    if (!selectedState) errors.state = "Please select a state";
    if (!selectedCity) errors.city = "Please select a city";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setError("");
    setUploading(true);
    setUploadProgress(0);
    setUploadComplete(false);
    setStep(2);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE || "http://localhost:8080";
      // ✅ FIX: Force token refresh to get updated email_verified claim
      const idToken = await getIdToken(true);
      const res = await fetch(`${baseUrl}/api/mux/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({
          filename: videoFile.name,
          contentType: videoFile.type,
          title,
          description,
          city: selectedCity,
          state: selectedState,
        }),
      });
      if (!res.ok) throw new Error("Failed to get upload URL");
      const { uploadUrl } = await res.json();

      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", videoFile.type);

      // ✅ FIX: Add 5-minute timeout for uploads (300,000ms)
      // Prevents infinite loading on slow/flaky networks
      xhr.timeout = 300000; // 5 minutes

      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      };
      xhr.onload = function () {
        if (xhr.status === 200 || xhr.status === 201) {
          setUploadProgress(100);
          setUploadComplete(true);
          setTimeout(() => {
            setUploading(false);
          }, 400);
        } else {
          setError("Upload failed");
          setUploading(false);
        }
      };
      xhr.onerror = function () {
        setError("An error occurred during upload.");
        setUploading(false);
      };
      // ✅ FIX: Add timeout handler
      xhr.ontimeout = function () {
        setError("Upload timed out. Please check your connection and try again.");
        setUploading(false);
      };
      xhr.send(videoFile);
    } catch (err) {
      setError(err.message);
      setUploading(false);
    }
  };

  const resetAll = () => {
    // Clean up the blob URL to prevent memory leaks
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setStep(0);
    setUploadComplete(false);
    setVideoFile(null);
    setVideoPreview(null);
    setSelectedState("");
    setSelectedCity("");
    setTitle("");
    setDescription("");
    setCityOptions([]);
  };

  const stateItems = useMemo(() => stateOptions || [], []);

  // Show message if not logged in
  if (!currentUser) {
    return (
      <Card className="border border-default-200/30">
        <CardBody className="text-center py-8">
          <p className="text-default-500">Please sign in to upload videos.</p>
        </CardBody>
      </Card>
    );
  }

  // Show message if email not verified
  if (!currentUser.emailVerified) {
    return (
      <Card className="border border-default-200/30">
        <CardBody className="text-center py-8">
          <p className="text-default-500">Please verify your email address to upload videos.</p>
          <p className="text-default-400 text-sm mt-2">Check your inbox for the verification email.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
          {/* Step indicator (title removed to avoid duplication with modal header) */}
          <div className="flex items-center gap-1.5 sm:gap-3 overflow-x-auto">
              {["Select Video", "Add Details", "Upload"].map((label, idx) => (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                      step >= idx ? "bg-primary text-primary-foreground" : "bg-default-200 text-default-700"
                    }`}>
                      {idx + 1}
                    </div>
                    <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${step === idx ? "text-foreground" : "text-default-700"}`}>{label}</span>
                  </div>
                  {idx < 2 && <div className="h-px w-3 sm:w-6 bg-default-200 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>

          {/* Step 0: Select Video */}
          {step === 0 && !loadingMetadata && (
            <Card className="border border-default-200/30">
              <CardBody>
                <div
                  className="rounded-xl border-2 border-dashed border-default-300 bg-content1/40 p-4 sm:p-8 text-center cursor-pointer"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  onClick={() => document.getElementById("video-file-input")?.click()}
                >
                  <CloudArrowUpIcon className="w-10 h-10 sm:w-12 sm:h-12 text-primary mx-auto mb-2 sm:mb-3" />
                  <p className="text-sm sm:text-base text-foreground font-semibold">Click to browse</p>
                  <p className="text-xs sm:text-sm text-default-500">or drag your video here</p>
                  <p className="text-xs text-default-500 mt-2 px-2">MP4, MOV, AVI, or WebM</p>
                  <p className="text-xs text-warning mt-1 px-2">Max: 1080p, ≤ 4 min, ≤ 350 MB</p>
                  <input id="video-file-input" type="file" accept="video/*" className="hidden" onChange={onBrowse} />
                </div>
              </CardBody>
            </Card>
          )}

          {/* Loading Metadata */}
          {loadingMetadata && (
            <Card className="border border-default-200/30">
              <CardBody>
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="relative">
                    <VideoCameraIcon className="w-16 h-16 text-primary animate-pulse" />
                  </div>
                  <div className="text-center space-y-2">
                    <h4 className="text-lg font-semibold text-foreground">Processing Video</h4>
                    <p className="text-default-500 text-sm">Reading video metadata...</p>
                  </div>
                  <Progress
                    size="sm"
                    isIndeterminate
                    aria-label="Loading video metadata"
                    className="max-w-md"
                    color="primary"
                  />
                </div>
              </CardBody>
            </Card>
          )}

          {/* Step 1: Details */}
          {step === 1 && videoFile && (
            <Card className="border border-default-200/30">
              <CardBody className="p-3 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-6">
                  <div className="md:col-span-2 space-y-2 sm:space-y-3">
                    {videoPreview && (
                      <video src={videoPreview} controls className="w-full rounded-lg max-h-32 sm:max-h-48" />
                    )}
                    <div className="space-y-1">
                      <div className="text-xs sm:text-small text-foreground/80 truncate">{videoFile.name}</div>
                      <div className="text-xs text-foreground/60">Duration: {formatDuration(videoDuration)} • Size: {videoSize}MB</div>
                      <Button 
                        size="sm" 
                        variant="flat" 
                        color="danger" 
                        startContent={<XMarkIcon className="w-3 h-3 sm:w-4 sm:h-4" />} 
                        onPress={clearVideo}
                        className="text-xs sm:text-sm"
                      >
                        Remove Video
                      </Button>
                    </div>
                  </div>
                  <div className="md:col-span-3 space-y-3 sm:space-y-4">
                    {/* Warning for over-limit videos */}
                    {warning && (
                      <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
                        <p className="text-xs text-warning font-medium">{warning}</p>
                      </div>
                    )}

                    <Input
                      label="Video Title"
                      variant="bordered"
                      value={title}
                      onChange={(e) => setTitle(e.target.value.slice(0, VIDEO_TITLE_MAX_LENGTH))}
                      placeholder="e.g., Tesla Model 3 Road Trip to Arizona"
                      isRequired
                      isDisabled={uploading}
                      isInvalid={!!fieldErrors.title}
                      errorMessage={fieldErrors.title}
                      description={`${title.length}/${VIDEO_TITLE_MAX_LENGTH} characters (minimum 10 required)`}
                      maxLength={VIDEO_TITLE_MAX_LENGTH}
                      classNames={{
                        input: isMobile ? "tap-highlight-transparent text-base" : ""
                      }}
                    />
                    <Textarea
                      label="Description"
                      variant="bordered"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Ex: Tesla Model 3, Phoenix AZ, I-17 freeway, brake check - Be specific for better discoverability"
                      minRows={4}
                      isRequired
                      isDisabled={uploading}
                      isInvalid={!!fieldErrors.description}
                      errorMessage={fieldErrors.description}
                      description={`${description.length} characters (min 20) • Include: vehicle, location, road/highway, what happened`}
                      classNames={{
                        input: isMobile ? "tap-highlight-transparent text-base" : ""
                      }}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {isMobile ? (
                        // Native select for mobile - triggers iOS picker wheel
                        <>
                          <div className="flex flex-col gap-2">
                            <label className="text-sm text-foreground">
                              State <span className="text-danger">*</span>
                            </label>
                            <select
                              value={selectedState}
                              onChange={(e) => setSelectedState(e.target.value)}
                              disabled={uploading}
                              className={`
                                w-full px-3 py-2 text-base rounded-lg
                                border-2 ${fieldErrors.state ? 'border-danger' : 'border-default-200'}
                                bg-default-100 text-foreground
                                focus:outline-none focus:border-primary
                                disabled:opacity-50 disabled:cursor-not-allowed
                              `}
                            >
                              <option value="">Select state</option>
                              {stateItems.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                            {fieldErrors.state && (
                              <span className="text-xs text-danger">{fieldErrors.state}</span>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="text-sm text-foreground">
                              City <span className="text-danger">*</span>
                            </label>
                            <select
                              value={selectedCity}
                              onChange={(e) => setSelectedCity(e.target.value)}
                              disabled={!selectedState || uploading}
                              className={`
                                w-full px-3 py-2 text-base rounded-lg
                                border-2 ${fieldErrors.city ? 'border-danger' : 'border-default-200'}
                                bg-default-100 text-foreground
                                focus:outline-none focus:border-primary
                                disabled:opacity-50 disabled:cursor-not-allowed
                              `}
                            >
                              <option value="">Select city</option>
                              {cityOptions.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                            {fieldErrors.city && (
                              <span className="text-xs text-danger">{fieldErrors.city}</span>
                            )}
                          </div>
                        </>
                      ) : (
                        // HeroUI Select for desktop
                        <>
                          <Select
                            label="State"
                            variant="bordered"
                            placeholder="Select state"
                            selectedKeys={selectedState ? new Set([selectedState]) : new Set([])}
                            onSelectionChange={(keys) => setSelectedState(keys.values().next().value || "")}
                            isRequired
                            isDisabled={uploading}
                            isInvalid={!!fieldErrors.state}
                            errorMessage={fieldErrors.state}
                            classNames={{
                              trigger: "text-foreground",
                              value: "text-foreground",
                              label: "text-foreground",
                              popoverContent: "bg-content1"
                            }}
                          >
                            {stateItems.map((s) => (
                              <SelectItem
                                key={s.value}
                                value={s.value}
                                className="text-foreground"
                              >
                                {s.label}
                              </SelectItem>
                            ))}
                          </Select>
                          <Select
                            label="City"
                            variant="bordered"
                            placeholder="Select city"
                            selectedKeys={selectedCity ? new Set([selectedCity]) : new Set([])}
                            onSelectionChange={(keys) => setSelectedCity(keys.values().next().value || "")}
                            isRequired
                            isDisabled={!selectedState || uploading}
                            isInvalid={!!fieldErrors.city}
                            errorMessage={fieldErrors.city}
                            classNames={{
                              trigger: "text-foreground",
                              value: "text-foreground",
                              label: "text-foreground",
                              popoverContent: "bg-content1"
                            }}
                          >
                            {cityOptions.map((c) => (
                              <SelectItem
                                key={c.value}
                                value={c.value}
                                className="text-foreground"
                              >
                                {c.label}
                              </SelectItem>
                            ))}
                          </Select>
                        </>
                      )}
                    </div>

                    {/* Content Rules Checkbox */}
                    <div className="pt-3 sm:pt-4 border-t border-default-200">
                      <Checkbox
                        isSelected={contentRulesAccepted}
                        onValueChange={setContentRulesAccepted}
                        size="sm"
                        classNames={{
                          label: "text-xs sm:text-sm text-foreground"
                        }}
                      >
                        <span className="text-xs sm:text-sm leading-tight">
                          I confirm this video contains <strong>no graphic gore or nudity</strong>, and I have the rights to upload this content
                        </span>
                      </Checkbox>
                    </div>

                    <div className="flex gap-2 sm:gap-3 flex-wrap">
                      <Button 
                        variant="flat" 
                        onPress={() => setStep(0)} 
                        isDisabled={uploading}
                        size="sm"
                        className="sm:size-md flex-1 sm:flex-initial"
                      >
                        Back
                      </Button>
                      <Button
                        color="primary"
                        onPress={doUpload}
                        isLoading={uploading}
                        isDisabled={!contentRulesAccepted}
                        startContent={<CloudArrowUpIcon className="w-4 h-4" />}
                        size="sm"
                        className="sm:size-md flex-1 sm:flex-initial"
                      >
                        {uploading ? "Uploading..." : "Upload Video"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Step 2: Upload */}
          {step === 2 && (
            <Card className="border border-default-200/30">
              <CardBody>
                {!uploadComplete ? (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold">Uploading Your Video</h4>
                    <Progress aria-label="Upload progress" value={uploadProgress} color="primary" showValueLabel className="max-w-xl" />
                    <p className="text-default-500 text-sm">Please don't close this window while uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <CheckCircleIcon className="w-14 h-14 text-success" />
                    <h3 className="text-success text-xl font-semibold">Upload Successful!</h3>
                    <p className="text-default-500 text-sm">Your video is now being processed and will be available shortly.</p>
                    <Button color="primary" onPress={resetAll} className="mt-2">Upload Another Video</Button>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Error */}
          {error && (
            <Card className="border border-danger/20">
              <CardBody className="flex flex-col gap-3">
                <p className="text-danger text-sm">{error}</p>
                {(error.includes("failed") || error.includes("error")) && step === 2 && (
                  <Button
                    size="sm"
                    color="danger"
                    variant="flat"
                    onClick={() => {
                      setError('');
                      setStep(1);
                    }}
                  >
                    Try Again
                  </Button>
                )}
              </CardBody>
            </Card>
          )}
      </div>
    </>
  );
};

export default VideoUploadHeroUI;
